import { useEffect, useState, useCallback, useMemo } from 'react'
import * as LucideIcons from 'lucide-react'
import {
  Clock, MapPin, Beer, Radio, Trophy, Wifi, LogIn, ShoppingBag,
  Crown, Medal, Sparkles, ArrowRight, CalendarClock, Server, Swords, Users,
  AlertTriangle,
} from 'lucide-react'
import { programmeAPI, kassaAPI, visionAPI, imagecompoAPI, tournamentsAPI, statsAPI, gameServersAPI } from '@/lib/api'
import { useProgrammeSocket } from '@/lib/programmeSocket'
import { useKassaSocket } from '@/lib/kassaSocket'
import usePageTitle from '@/lib/usePageTitle'

/* Schermen voor de beamers in de zaal.

   Alles hier is gemaakt om van vijf meter afstand gelezen te worden: grote
   letters, weinig woorden, hoog contrast. Ze verversen zichzelf, want er staat
   niemand naast om F5 te drukken.

   Ze delen één schil (ZaalScherm) zodat kop, klok en achtergrond overal
   hetzelfde aanvoelen, ook als iemand tussen schermen schakelt. */

/* ── Gedeelde schil ──────────────────────────────────────────────── */

function ZaalScherm({ eyebrow, titel, accent = '#B4FF39', children, voet }) {
  const [nu, setNu] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNu(new Date()), 10_000)
    return () => clearInterval(t)
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col p-[3vmin]">
      <header className="flex items-baseline gap-[2vmin] border-b border-border pb-[1.5vmin] shrink-0">
        <span className="font-mono uppercase tracking-[0.25em] text-muted-foreground"
              style={{ fontSize: 'clamp(.7rem, 1.4vmin, 1.1rem)' }}>
          {eyebrow}
        </span>
        <h1 className="font-bold leading-none tracking-tight"
            style={{ fontSize: 'clamp(1.4rem, 3.4vmin, 3rem)', color: accent }}>
          {titel}
        </h1>
        <span className="ml-auto font-mono tabular-nums text-muted-foreground"
              style={{ fontSize: 'clamp(1rem, 2.6vmin, 2.2rem)' }}>
          {nu.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </header>

      <main className="flex-1 min-h-0 py-[2vmin]">{children}</main>

      {voet && (
        <footer className="border-t border-border pt-[1.2vmin] font-mono uppercase tracking-[0.2em] text-muted-foreground shrink-0"
                style={{ fontSize: 'clamp(.6rem, 1.2vmin, .95rem)' }}>
          {voet}
        </footer>
      )}
    </div>
  )
}

/** Herbruikbare hint voor een scherm zonder inhoud — nooit een leeg vlak op een beamer. */
function Leeg({ icon: Icon = Clock, tekst }) {
  return (
    <div className="h-full grid place-items-center text-center">
      <div>
        <Icon className="mx-auto text-muted-foreground mb-[2vmin]"
              style={{ width: '8vmin', height: '8vmin' }} />
        <p className="text-muted-foreground" style={{ fontSize: 'clamp(1rem, 2.2vmin, 1.8rem)' }}>
          {tekst}
        </p>
      </div>
    </div>
  )
}

const tijd = (iso) => iso
  ? new Date(iso).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })
  : ''

const TRACKS = {
  zaal:    { label: 'Zaal',                    icon: MapPin,        kleur: '#7BC8FF' },
  bar:     { label: 'Bar',                     icon: Beer,          kleur: '#FFB57A' },
  show:    { label: 'Show',                    icon: Radio,         kleur: '#B4FF39' },
  compo:   { label: 'Compo',                   icon: Trophy,        kleur: '#C77BFF' },
  kritiek: { label: 'Kritieke aankondigingen', icon: AlertTriangle, kleur: '#FF5A5A' },
}

/* ── 1. Nu & straks ──────────────────────────────────────────────── */

export function SchermNuStraks() {
  usePageTitle('Nu & straks')
  const [data, setData] = useState({ now: [], next: [] })

  const laad = useCallback(() => {
    programmeAPI.getNow()
      .then(r => setData({ now: r.data?.now ?? [], next: r.data?.next ?? [] }))
      .catch(() => {})
  }, [])

  useEffect(() => { laad() }, [laad])
  useProgrammeSocket(laad)
  // De tijdlijn schuift door zonder dat er iets verandert aan de agenda.
  useEffect(() => {
    const t = setInterval(laad, 60_000)
    return () => clearInterval(t)
  }, [laad])

  const { now, next } = data
  const leeg = now.length === 0 && next.length === 0

  return (
    <ZaalScherm eyebrow="Programma" titel="Nu & straks" voet="Volledige agenda op lanserver.local/programma">
      {leeg ? (
        <Leeg icon={CalendarClock} tekst="Nog niets ingepland." />
      ) : (
        <div className="h-full flex flex-col gap-[2.5vmin]">
          {/* Wat er nu loopt — dit is waar het oog eerst landt */}
          {now.length > 0 && (
            <div className="grid gap-[1.5vmin]"
                 style={{ gridTemplateColumns: `repeat(${Math.min(now.length, 3)}, minmax(0,1fr))` }}>
              {now.slice(0, 3).map(s => {
                const t = TRACKS[s.track] ?? TRACKS.zaal
                const Icon = t.icon
                return (
                  <div key={s.id} className="rounded-[4px] border-2 bg-card p-[2.5vmin]"
                       style={{ borderColor: t.kleur }}>
                    <div className="flex items-center gap-[1vmin] mb-[1.5vmin]">
                      <Icon style={{ width: '3vmin', height: '3vmin', color: t.kleur }} />
                      <span className="font-mono uppercase tracking-[0.2em]"
                            style={{ fontSize: 'clamp(.65rem, 1.3vmin, 1rem)', color: t.kleur }}>
                        {t.label} · bezig
                      </span>
                    </div>
                    <div className="font-bold leading-tight"
                         style={{ fontSize: 'clamp(1.3rem, 3.6vmin, 3.4rem)' }}>
                      {s.title}
                    </div>
                    {s.description && (
                      <p className="text-muted-foreground mt-[1vmin] leading-snug"
                         style={{ fontSize: 'clamp(.85rem, 1.7vmin, 1.4rem)' }}>
                        {s.description}
                      </p>
                    )}
                    {s.ends_at && (
                      <div className="font-mono tabular-nums text-muted-foreground mt-[1.5vmin]"
                           style={{ fontSize: 'clamp(.8rem, 1.6vmin, 1.3rem)' }}>
                        tot {tijd(s.ends_at)}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Wat eraan komt */}
          {next.length > 0 && (
            <div className="flex-1 min-h-0">
              <div className="font-mono uppercase tracking-[0.25em] text-muted-foreground mb-[1.2vmin]"
                   style={{ fontSize: 'clamp(.65rem, 1.3vmin, 1rem)' }}>
                Hierna
              </div>
              <div className="space-y-[1vmin]">
                {next.map(s => {
                  const t = TRACKS[s.track] ?? TRACKS.zaal
                  const Icon = t.icon
                  return (
                    <div key={s.id}
                         className="flex items-center gap-[2vmin] rounded-[4px] border border-border bg-card px-[2vmin] py-[1.5vmin]"
                         style={{ borderLeft: `3px solid ${t.kleur}` }}>
                      <span className="font-mono tabular-nums font-bold shrink-0"
                            style={{ fontSize: 'clamp(1.1rem, 2.6vmin, 2.2rem)' }}>
                        {tijd(s.starts_at)}
                      </span>
                      <Icon className="shrink-0" style={{ width: '2.4vmin', height: '2.4vmin', color: t.kleur }} />
                      <span className="font-semibold truncate"
                            style={{ fontSize: 'clamp(1rem, 2.2vmin, 1.9rem)' }}>
                        {s.title}
                      </span>
                      {s.is_highlight && (
                        <span className="ml-auto font-mono uppercase tracking-widest text-gold shrink-0"
                              style={{ fontSize: 'clamp(.6rem, 1.2vmin, .9rem)' }}>
                          hoofdmoment
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </ZaalScherm>
  )
}

/* ── 2. Bar ──────────────────────────────────────────────────────── */

export function SchermBar() {
  usePageTitle('Bar')
  const [menu, setMenu] = useState(null)
  const [top, setTop]   = useState([])

  const laad = useCallback(() => {
    kassaAPI.getMenu().then(r => setMenu(r.data)).catch(() => {})
    kassaAPI.getLeaderboard()
      .then(r => setTop((r.data?.leaderboard ?? []).slice(0, 8)))
      .catch(() => {})
  }, [])

  useEffect(() => { laad() }, [laad])
  useKassaSocket(laad)

  const perCategorie = useMemo(() => {
    const g = {}
    for (const it of (menu?.items ?? [])) {
      if (!it.enabled) continue
      const c = it.category || 'overig'
      ;(g[c] ??= []).push(it)
    }
    return Object.entries(g).slice(0, 3)
  }, [menu])

  const event = menu?.activeEvent

  return (
    <ZaalScherm eyebrow="Bar" titel={event?.name || 'Bar'} accent="#FFB57A"
                voet="Bestellen kan via lanserver.local/bestellen">
      {!menu ? (
        <Leeg icon={Beer} tekst="Menu wordt geladen…" />
      ) : (
        <div className="h-full grid gap-[2.5vmin]" style={{ gridTemplateColumns: '1.6fr 1fr' }}>
          {/* Menu */}
          <div className="min-h-0 overflow-hidden">
            {perCategorie.length === 0 ? (
              <Leeg icon={ShoppingBag} tekst="Geen menu actief." />
            ) : (
              <div className="space-y-[2vmin]">
                {perCategorie.map(([cat, items]) => (
                  <div key={cat}>
                    <div className="font-mono uppercase tracking-[0.25em] text-peach mb-[1vmin]"
                         style={{ fontSize: 'clamp(.65rem, 1.3vmin, 1rem)' }}>
                      {cat}
                    </div>
                    <div className="grid gap-[.8vmin]"
                         style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 22vmin), 1fr))' }}>
                      {items.slice(0, 12).map(it => (
                        <div key={it.id}
                             className="flex items-baseline gap-[1vmin] rounded-[4px] border border-border bg-card px-[1.4vmin] py-[1vmin]">
                          <span className="truncate flex-1" style={{ fontSize: 'clamp(.85rem, 1.8vmin, 1.5rem)' }}>
                            {it.name}
                          </span>
                          <span className="font-mono tabular-nums font-bold text-peach shrink-0"
                                style={{ fontSize: 'clamp(.85rem, 1.8vmin, 1.5rem)' }}>
                            €{(it.price_cents / 100).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top drinkers — een ranglijst maakt van bestellen een wedstrijd */}
          <div className="min-h-0 border-l border-border pl-[2.5vmin]">
            <div className="font-mono uppercase tracking-[0.25em] text-muted-foreground mb-[1.2vmin]"
                 style={{ fontSize: 'clamp(.65rem, 1.3vmin, 1rem)' }}>
              Meest gedronken
            </div>
            {top.length === 0 ? (
              <p className="text-muted-foreground" style={{ fontSize: 'clamp(.9rem, 1.8vmin, 1.4rem)' }}>
                Nog niemand. Wie begint?
              </p>
            ) : (
              <div className="space-y-[.8vmin]">
                {top.map((r, i) => (
                  <div key={i} className="flex items-baseline gap-[1.2vmin]">
                    <span className="font-mono tabular-nums text-muted-foreground w-[3vmin] shrink-0"
                          style={{ fontSize: 'clamp(.8rem, 1.6vmin, 1.2rem)' }}>
                      {i + 1}
                    </span>
                    <span className="truncate flex-1"
                          style={{ fontSize: 'clamp(.9rem, 2vmin, 1.7rem)',
                                   color: i === 0 ? '#E4C265' : undefined,
                                   fontWeight: i === 0 ? 700 : 400 }}>
                      {r.customer?.name ?? r.name ?? '—'}
                    </span>
                    <span className="font-mono tabular-nums shrink-0"
                          style={{ fontSize: 'clamp(.9rem, 2vmin, 1.7rem)' }}>
                      {r.total_items ?? r.count ?? r.total ?? 0}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </ZaalScherm>
  )
}

/* ── 3. Welkom ───────────────────────────────────────────────────── */

export function SchermWelkom() {
  usePageTitle('Welkom')
  const [branding, setBranding] = useState(null)

  useEffect(() => {
    fetch('/api/settings/branding')
      .then(r => r.json())
      .then(d => setBranding(d?.branding ?? null))
      .catch(() => {})
  }, [])

  const naam = branding?.site_name || 'LAN Server'
  const host = typeof window !== 'undefined' ? window.location.host : 'lanserver.local'

  return (
    <ZaalScherm eyebrow="Welkom" titel={naam} voet={branding?.site_footer || ''}>
      <div className="h-full grid place-items-center">
        <div className="w-full max-w-[80vmin] space-y-[3vmin]">

          <Stap nummer="1" icon={Wifi} titel="Sluit aan op het netwerk"
                tekst="Kabel in het stopcontact naast je plek. Je krijgt vanzelf een adres." />

          <Stap nummer="2" icon={LogIn} titel={`Surf naar ${host}`}
                tekst="Log in met de gegevens op je kaartje. Bij je eerste login kies je zelf een nieuw wachtwoord." />

          <Stap nummer="3" icon={ShoppingBag} titel="Bestellen doe je in de app"
                tekst="Drank en eten bestel je via Bar & bestellen. Je scant je kaart aan de bar." />

          <Stap nummer="4" icon={Trophy} titel="Schrijf je in voor de compo's"
                tekst="Toernooien, Kempenvision en de Image Compo staan in het menu links." />

        </div>
      </div>
    </ZaalScherm>
  )
}

function Stap({ nummer, icon: Icon, titel, tekst }) {
  return (
    <div className="flex items-start gap-[2.5vmin]">
      <div className="rounded-[4px] border border-border bg-card grid place-items-center shrink-0 font-mono font-bold text-acid"
           style={{ width: '7vmin', height: '7vmin', fontSize: 'clamp(1rem, 2.4vmin, 2rem)',
                    borderLeft: '3px solid rgb(var(--acid-rgb))' }}>
        {nummer}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-[1.2vmin]">
          <Icon className="text-acid shrink-0" style={{ width: '2.6vmin', height: '2.6vmin' }} />
          <span className="font-bold" style={{ fontSize: 'clamp(1.1rem, 2.8vmin, 2.4rem)' }}>{titel}</span>
        </div>
        <p className="text-muted-foreground mt-[.5vmin] leading-snug"
           style={{ fontSize: 'clamp(.85rem, 1.9vmin, 1.5rem)' }}>
          {tekst}
        </p>
      </div>
    </div>
  )
}

/* ── 4. Scores ───────────────────────────────────────────────────── */

export function SchermScores() {
  usePageTitle('Scores')
  const [kv, setKv]   = useState(null)
  const [ic, setIc]   = useState(null)
  const [lb, setLb]   = useState([])

  const laad = useCallback(() => {
    visionAPI.getState().then(r => setKv(r.data)).catch(() => setKv(null))
    imagecompoAPI.getState().then(r => setIc(r.data)).catch(() => setIc(null))
    statsAPI.getLeaderboard?.()
      .then(r => setLb((r.data?.leaderboard ?? r.data ?? []).slice(0, 8)))
      .catch(() => setLb([]))
  }, [])

  useEffect(() => { laad() }, [laad])
  useEffect(() => {
    const t = setInterval(laad, 20_000)
    return () => clearInterval(t)
  }, [laad])

  // Standen uit de shows halen; de vorm verschilt per module, dus voorzichtig lezen.
  const kvStand = (kv?.standings ?? kv?.state?.standings ?? []).slice(0, 6)
  const icStand = (ic?.standings ?? ic?.state?.standings ?? []).slice(0, 6)

  const kolommen = [
    kvStand.length > 0 && { titel: 'Kempenvision', kleur: '#B4FF39', icon: Radio,     rijen: kvStand },
    icStand.length > 0 && { titel: 'Image Compo',  kleur: '#C77BFF', icon: Sparkles,  rijen: icStand },
    lb.length > 0       && { titel: 'Aller tijden', kleur: '#E4C265', icon: Trophy,   rijen: lb },
  ].filter(Boolean)

  return (
    <ZaalScherm eyebrow="Stand" titel="Wie staat waar" accent="#E4C265"
                voet="Volledige uitslagen op lanserver.local">
      {kolommen.length === 0 ? (
        <Leeg icon={Trophy} tekst="Nog geen uitslagen. De eerste punten moeten nog vallen." />
      ) : (
        <div className="h-full grid gap-[2.5vmin]"
             style={{ gridTemplateColumns: `repeat(${kolommen.length}, minmax(0,1fr))` }}>
          {kolommen.map(k => {
            const Icon = k.icon
            return (
              <div key={k.titel} className="min-h-0">
                <div className="flex items-center gap-[1.2vmin] mb-[1.5vmin] pb-[1vmin] border-b"
                     style={{ borderColor: k.kleur }}>
                  <Icon style={{ width: '2.8vmin', height: '2.8vmin', color: k.kleur }} />
                  <span className="font-mono uppercase tracking-[0.2em]"
                        style={{ fontSize: 'clamp(.7rem, 1.4vmin, 1.1rem)', color: k.kleur }}>
                    {k.titel}
                  </span>
                </div>
                <div className="space-y-[1vmin]">
                  {k.rijen.map((r, i) => (
                    <div key={i} className="flex items-baseline gap-[1.2vmin]">
                      <Plaats i={i} />
                      <span className="truncate flex-1"
                            style={{ fontSize: 'clamp(.95rem, 2.1vmin, 1.8rem)',
                                     fontWeight: i === 0 ? 700 : 400 }}>
                        {r.team_name ?? r.name ?? r.display_name ?? r.username ?? '—'}
                      </span>
                      <span className="font-mono tabular-nums font-bold shrink-0"
                            style={{ fontSize: 'clamp(.95rem, 2.1vmin, 1.8rem)',
                                     color: i === 0 ? k.kleur : undefined }}>
                        {r.total ?? r.points ?? r.score ?? r.tournaments_won ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </ZaalScherm>
  )
}

function Plaats({ i }) {
  const stijl = { width: '2.6vmin', height: '2.6vmin' }
  if (i === 0) return <Crown className="text-gold shrink-0" style={stijl} />
  if (i === 1) return <Medal className="text-muted-foreground shrink-0" style={stijl} />
  if (i === 2) return <Medal className="text-peach shrink-0" style={stijl} />
  return (
    <span className="font-mono tabular-nums text-muted-foreground text-center shrink-0"
          style={{ width: '2.6vmin', fontSize: 'clamp(.75rem, 1.5vmin, 1.15rem)' }}>
      {i + 1}
    </span>
  )
}


/* ── 5. Game servers ─────────────────────────────────────────────── */

export function SchermServers() {
  usePageTitle('Servers')
  const [servers, setServers] = useState([])

  const laad = useCallback(() => {
    gameServersAPI.getServers?.()
      .then(r => setServers(r.data?.servers ?? r.data ?? []))
      .catch(() => {})
  }, [])

  useEffect(() => { laad() }, [laad])
  // De monitor ververst serverstatus elke 30 seconden; wij lopen daar iets achteraan.
  useEffect(() => {
    const t = setInterval(laad, 30_000)
    return () => clearInterval(t)
  }, [laad])

  const online = servers.filter(s => s.status === 'online')

  return (
    <ZaalScherm eyebrow="Servers" titel="Wat draait er" accent="#7BC8FF"
                voet="Verbind via het adres onder de servernaam">
      {servers.length === 0 ? (
        <Leeg icon={Server} tekst="Geen servers geconfigureerd." />
      ) : (
        <div className="h-full grid gap-[1.5vmin] content-start"
             style={{ gridTemplateColumns: `repeat(${Math.min(Math.max(servers.length, 1), 3)}, minmax(0,1fr))` }}>
          {servers.slice(0, 9).map(sv => {
            const aan = sv.status === 'online'
            const vol = (sv.current_players ?? 0) >= (sv.max_players ?? 99)
            return (
              <div key={sv.id}
                   className="rounded-[4px] border bg-card p-[2vmin]"
                   style={{ borderColor: aan ? '#61E294' : 'var(--border)',
                            opacity: aan ? 1 : .45 }}>
                <div className="flex items-center gap-[1vmin] mb-[1vmin]">
                  <span className="rounded-full shrink-0"
                        style={{ width: '1.2vmin', height: '1.2vmin',
                                 background: aan ? '#61E294' : '#94A3B8' }} />
                  <span className="font-mono uppercase tracking-[0.2em] text-muted-foreground"
                        style={{ fontSize: 'clamp(.6rem, 1.2vmin, .95rem)' }}>
                    {sv.game_type ?? 'game'}
                  </span>
                  {vol && aan && (
                    <span className="ml-auto font-mono uppercase tracking-widest text-peach"
                          style={{ fontSize: 'clamp(.6rem, 1.2vmin, .9rem)' }}>vol</span>
                  )}
                </div>

                <div className="font-bold leading-tight truncate"
                     style={{ fontSize: 'clamp(1.1rem, 2.8vmin, 2.4rem)' }}>
                  {sv.name}
                </div>

                <div className="font-mono text-muted-foreground truncate mt-[.5vmin]"
                     style={{ fontSize: 'clamp(.75rem, 1.5vmin, 1.2rem)' }}>
                  {sv.ip_address}{sv.port ? `:${sv.port}` : ''}
                </div>

                {aan && (
                  <div className="flex items-baseline gap-[1vmin] mt-[1.2vmin]">
                    <Users style={{ width: '2vmin', height: '2vmin' }} className="text-live" />
                    <span className="font-mono tabular-nums font-bold text-live"
                          style={{ fontSize: 'clamp(1.1rem, 2.6vmin, 2.2rem)' }}>
                      {sv.current_players ?? 0}
                    </span>
                    <span className="font-mono tabular-nums text-muted-foreground"
                          style={{ fontSize: 'clamp(.85rem, 1.8vmin, 1.4rem)' }}>
                      / {sv.max_players ?? '?'}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </ZaalScherm>
  )
}

/* ── 6. Toernooi live ────────────────────────────────────────────── */

export function SchermToernooi() {
  usePageTitle('Toernooi')
  const [toernooien, setToern] = useState([])
  const [matches, setMatches]  = useState([])

  const laad = useCallback(async () => {
    try {
      const r = await tournamentsAPI.getTournaments()
      const lijst = r.data?.tournaments ?? r.data ?? []
      setToern(lijst)

      // Van het eerste lopende toernooi de matches ophalen voor het speelschema.
      const live = lijst.find(t => ['active', 'in_progress', 'ongoing'].includes(t.status))
      if (live && tournamentsAPI.getTournament) {
        const d = await tournamentsAPI.getTournament(live.id)
        setMatches(d.data?.matches ?? [])
      } else {
        setMatches([])
      }
    } catch { /* stil: op een beamer wil je geen foutmelding */ }
  }, [])

  useEffect(() => { laad() }, [laad])
  useEffect(() => {
    const t = setInterval(laad, 15_000)
    return () => clearInterval(t)
  }, [laad])

  const live = toernooien.filter(t => ['active', 'in_progress', 'ongoing'].includes(t.status))
  const bezig = matches.filter(m => m.status === 'in_progress' || m.status === 'live')
  const straks = matches.filter(m => m.status === 'pending' || m.status === 'scheduled').slice(0, 6)

  return (
    <ZaalScherm eyebrow="Toernooi" titel={live[0]?.name ?? 'Toernooien'} accent="#E4C265"
                voet="Volledig schema op lanserver.local/tournaments">
      {live.length === 0 ? (
        <Leeg icon={Trophy} tekst="Geen toernooi bezig." />
      ) : (
        <div className="h-full flex flex-col gap-[2.5vmin]">

          {bezig.length > 0 && (
            <div className="grid gap-[1.5vmin]"
                 style={{ gridTemplateColumns: `repeat(${Math.min(bezig.length, 2)}, minmax(0,1fr))` }}>
              {bezig.slice(0, 2).map(m => (
                <div key={m.id} className="rounded-[4px] border-2 border-gold bg-card p-[2.5vmin]">
                  <div className="font-mono uppercase tracking-[0.2em] text-gold mb-[1.5vmin]"
                       style={{ fontSize: 'clamp(.65rem, 1.3vmin, 1rem)' }}>
                    nu bezig{m.round ? ` · ronde ${m.round}` : ''}
                  </div>
                  <div className="flex items-center gap-[2vmin]">
                    <Kant naam={m.participant1_name ?? m.team1_name ?? 'TBD'} score={m.score1} />
                    <Swords className="text-muted-foreground shrink-0"
                            style={{ width: '3vmin', height: '3vmin' }} />
                    <Kant naam={m.participant2_name ?? m.team2_name ?? 'TBD'} score={m.score2} rechts />
                  </div>
                </div>
              ))}
            </div>
          )}

          {straks.length > 0 && (
            <div className="flex-1 min-h-0">
              <div className="font-mono uppercase tracking-[0.25em] text-muted-foreground mb-[1.2vmin]"
                   style={{ fontSize: 'clamp(.65rem, 1.3vmin, 1rem)' }}>
                Hierna
              </div>
              <div className="space-y-[.9vmin]">
                {straks.map(m => (
                  <div key={m.id}
                       className="flex items-center gap-[1.5vmin] rounded-[4px] border border-border bg-card px-[2vmin] py-[1.2vmin]">
                    <span className="font-mono tabular-nums text-muted-foreground shrink-0"
                          style={{ fontSize: 'clamp(.75rem, 1.5vmin, 1.15rem)' }}>
                      R{m.round ?? '?'}
                    </span>
                    <span className="truncate flex-1 text-right"
                          style={{ fontSize: 'clamp(.9rem, 2vmin, 1.7rem)' }}>
                      {m.participant1_name ?? m.team1_name ?? 'TBD'}
                    </span>
                    <span className="font-mono text-muted-foreground shrink-0"
                          style={{ fontSize: 'clamp(.75rem, 1.5vmin, 1.15rem)' }}>vs</span>
                    <span className="truncate flex-1"
                          style={{ fontSize: 'clamp(.9rem, 2vmin, 1.7rem)' }}>
                      {m.participant2_name ?? m.team2_name ?? 'TBD'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bezig.length === 0 && straks.length === 0 && (
            <Leeg icon={Swords} tekst="Nog geen wedstrijden ingedeeld." />
          )}
        </div>
      )}
    </ZaalScherm>
  )
}

function Kant({ naam, score, rechts = false }) {
  return (
    <div className={`flex-1 min-w-0 ${rechts ? 'text-left' : 'text-right'}`}>
      <div className="font-bold truncate leading-tight"
           style={{ fontSize: 'clamp(1.1rem, 2.8vmin, 2.4rem)' }}>
        {naam}
      </div>
      {score !== undefined && score !== null && (
        <div className="font-mono tabular-nums font-bold text-gold"
             style={{ fontSize: 'clamp(1.6rem, 4vmin, 3.4rem)' }}>
          {score}
        </div>
      )}
    </div>
  )
}
